/**
 * Task Lookup — paste any code, see everything that happened to the task.
 * Spec: docs/superpowers/specs/2026-09-29-task-lookup-design.md (parent repo).
 */
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, AlertCircle, ChevronRight, ChevronDown, Search } from 'lucide-react';
import { Card, Badge, Button, Input } from '../components/ui';
import { JsonViewer } from '../components/ui/JsonViewer';
import { lookupTask, LookupError, type TimelineEvent, type TimelineSource } from '../services/taskLookup.api';
import { cn } from '../utils/cn';

const SOURCE_LABEL: Record<TimelineSource, string> = {
  core_task: 'CORE', core_item: 'CORE', core_order: 'ORDER', app_tap: 'APP', fleet_api: 'FLEET', fleet_route: 'FLEET',
};
const SOURCE_COLOR: Record<TimelineSource, string> = {
  core_task: 'bg-purple-100 text-purple-800',
  core_item: 'bg-purple-100 text-purple-800',
  core_order: 'bg-amber-100 text-amber-800',
  app_tap: 'bg-emerald-100 text-emerald-800',
  fleet_api: 'bg-blue-100 text-blue-800',
  fleet_route: 'bg-blue-100 text-blue-800',
};
const UNAVAILABLE_TEXT: Record<TimelineSource, string> = {
  core_task: 'Task status history could not be read',
  core_item: 'Pickup/RTO history could not be read',
  core_order: 'Order history could not be read',
  app_tap: 'Driver app taps could not be read',
  fleet_api: 'Fleet calls to core could not be read',
  fleet_route: 'Fleet route data could not be read',
};
const FILTERS: { key: string; label: string; sources: TimelineSource[] | null }[] = [
  { key: 'all', label: 'All', sources: null },
  { key: 'core', label: 'Core', sources: ['core_task', 'core_item'] },
  { key: 'orders', label: 'Orders', sources: ['core_order'] },
  { key: 'app', label: 'App taps', sources: ['app_tap'] },
  { key: 'fleet', label: 'Fleet', sources: ['fleet_api', 'fleet_route'] },
];

/** Baghdad is UTC+3 all year. */
const baghdad = (iso: string) => {
  const d = new Date(Date.parse(iso) + 3 * 3_600_000).toISOString();
  return { date: d.slice(0, 10), time: d.slice(11, 19) };
};

function errorText(err: unknown): string {
  if (err instanceof LookupError && err.status === 400) return 'This does not look like a task, order, pickup or RTO code.';
  if (err instanceof LookupError && err.status === 404) return 'No task found for this code.';
  return 'The lookup failed. Try again, or send the code to the Fleet team.';
}

function EventRow({ e }: { e: TimelineEvent }) {
  const [open, setOpen] = useState(false);
  const t = baghdad(e.at);
  const who = e.actor
    ? [e.actor.type, e.actor.name ?? (e.actor.id != null ? `#${e.actor.id}` : null)].filter(Boolean).join(' ')
    : '';
  return (
    <li className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50"
      >
        <span className="w-12 shrink-0 text-xs text-gray-500">{t.date.slice(5)}</span>
        <span className="w-20 shrink-0 font-mono">{t.time}</span>
        <span className={cn('w-16 shrink-0 rounded px-1.5 py-0.5 text-center text-xs font-semibold', SOURCE_COLOR[e.source])}>
          {SOURCE_LABEL[e.source]}
        </span>
        <span className="flex-1">{e.title}</span>
        <span className="text-xs text-gray-600" dir="auto">{who}</span>
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3">
          {/* JsonViewer's colours are made for a dark surface, as on API Logs. */}
          <div className="max-h-96 overflow-auto rounded-lg bg-gray-900 p-3">
            <JsonViewer data={e.raw} defaultExpanded />
          </div>
        </div>
      )}
    </li>
  );
}

export default function TaskLookup() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const task = params.get('task') ?? undefined;
  const [input, setInput] = useState(q);
  const [filter, setFilter] = useState('all');

  const { data, error, isFetching } = useQuery({
    queryKey: ['task-lookup', q, task],
    queryFn: () => lookupTask(q, task),
    enabled: q.trim().length > 0,
    retry: false,
  });

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (input.trim()) setParams({ q: input.trim() });
  };

  const sources = FILTERS.find((f) => f.key === filter)?.sources;
  const events = data?.timeline.filter((e) => !sources || sources.includes(e.source)) ?? [];

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-2xl font-semibold">Task Lookup</h1>

      <form onSubmit={submit} className="flex gap-2">
        <div className="flex-1">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste any code: BXT / BXO / BXRTO / BXPU / uid"
            className="w-full"
          />
        </div>
        <Button type="submit" disabled={isFetching}>
          <Search className="mr-1 h-4 w-4" />
          {isFetching ? 'Looking…' : 'Look up'}
        </Button>
      </form>

      {isFetching && !data && !error && (
        <Card className="text-sm text-gray-600">Looking up {q}… this can take a few seconds.</Card>
      )}

      {error && <Card className="text-sm text-red-700">{errorText(error)}</Card>}

      {data && (
        <>
          {data.matches.length > 1 && (
            <Card padding="sm" className="text-sm">
              <p className="mb-2 font-medium">
                This code matches {data.matches.length} tasks. Showing {data.task.platform_code}. Pick another:
              </p>
              <ul className="flex flex-wrap gap-2">
                {data.matches.map((m) => (
                  <li key={m.uid}>
                    <Link
                      to={`/task-lookup?q=${encodeURIComponent(q)}&task=${m.uid}`}
                      className={cn('rounded border px-2 py-1', m.uid === data.task.uid && 'border-purple-500 bg-purple-50')}
                    >
                      {m.platform_code} · {m.type} · {m.status}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge>{data.task.type}</Badge>
              <span className="font-mono">{data.task.platform_code}</span>
              {(data.task.merchant_name || data.task.name) && (
                <span className="font-semibold" dir="auto">{data.task.merchant_name || data.task.name}</span>
              )}
              {data.task.merchant_name && data.task.name && data.task.name !== data.task.merchant_name && (
                <span className="text-gray-500" dir="auto">Contact: {data.task.name}</span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-700">
              <span>
                Now: <b>{data.task.status.toUpperCase()}</b>
              </span>
              <span dir="auto">
                Driver: {data.task.driver_name ? `${data.task.driver_name} (${data.task.driver_id})` : 'none'}
              </span>
              {data.item && (
                <span>
                  {data.item.kind === 'pick-up' ? 'Pickup' : 'RTO'}: {data.item.code ?? data.item.uid} · {data.item.status}
                </span>
              )}
              {data.route && (
                <span>
                  Route {data.route.route_name}, stop {data.route.sequence + 1}
                  {data.route.run_date ? ` · ${data.route.run_date}` : ''}
                </span>
              )}
            </div>
          </Card>

          {data.warnings.map((w) => (
            <Card
              key={w.rule}
              className={cn(
                'flex gap-2 text-sm',
                w.severity === 'error' ? 'border-red-300 bg-red-50 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-900'
              )}
            >
              {w.severity === 'error' ? <AlertCircle className="h-5 w-5 shrink-0" /> : <AlertTriangle className="h-5 w-5 shrink-0" />}
              <p dir="auto">{w.message}</p>
            </Card>
          ))}

          {data.unavailable.length > 0 && (
            <Card padding="sm" className="border-amber-300 bg-amber-50 text-sm text-amber-900">
              {[...new Set(data.unavailable.map((u) => u.source))].map((s) => (
                <p key={s}>{UNAVAILABLE_TEXT[s]} — the timeline may be missing these events.</p>
              ))}
            </Card>
          )}

          <Card padding="none">
            <div className="flex gap-2 border-b border-gray-100 p-3">
              {FILTERS.map((f) => (
                <Button
                  key={f.key}
                  type="button"
                  size="sm"
                  variant={filter === f.key ? 'primary' : 'outline'}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </Button>
              ))}
              <span className="ml-auto self-center text-xs text-gray-500">Baghdad time · newest first</span>
            </div>
            {events.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No events.</p>
            ) : (
              <ul>
                {events.map((e) => (
                  <EventRow key={e.id} e={e} />
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
